import React, { useState } from "react";
import Dropzone, { DropzoneState, FileRejection } from "react-dropzone";
import Papa from "papaparse";
import UploadedContainer from "./UploadedContainer";
import Image from "../images/Image";
import { handleDelete, handleUpload } from "./S3Function";
import ErrorMessage from "../ErrorMessages/ErrorMessage";
import RoundedLoader from "../loaders/RoundedLoader";
import InputLabel from "../inputfields/InputLabel";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import useTranslation from "../CustomHooks/useTranslation";

interface FileWithPreview extends File {
  preview: string;
}

type CSVRecord = Record<string, string | number>;

interface ParsedCSVData {
  records: Array<CSVRecord>;
}

interface S3ImageUploadProps {
  heading: string;
  disabled: boolean;
  UploadPath: string;
  upload_id: string;
  handleUpload: (
    id: string,
    value: string,
    file?: File,
    parsedData?: ParsedCSVData
  ) => void;
  uploadedFile: string;
  label?: string;
  errorMessage?: string;
  required?: boolean;
  id?: string;
  setCustomError?: (error: Record<string, string>) => void;
  paddingTop?: string;
  index?: number;
  uploadedContainerClass?: string;
  customWidth?: string;
  expectedColumns?: string[];
  customValidation?: (records: Array<CSVRecord>) => string | null;
}

const validateFile = (
  file: File,
  expectedColumns?: string[]
): string | null => {
  const imageFormats = ["image/jpeg", "image/png", "application/pdf"];
  const csvFormats = [
    "text/csv",
    "application/vnd.ms-excel",
    "application/csv",
  ];

  let validFormats = imageFormats;
  if (expectedColumns && expectedColumns.length > 0) {
    validFormats = csvFormats;
  }

  if (!validFormats.includes(file.type)) {
    return "Invalid file format.";
  }
  if (file.size > 10 * 1024 * 1024) {
    return "Upload Failed: File exceeds 10MB.";
  }
  return null;
};

const validateCSVStructure = (
  data: Array<Record<string, unknown>>,
  expectedColumns?: string[],
  customValidation?: (records: Array<CSVRecord>) => string | null
): { isValid: boolean; error?: string; parsedData?: ParsedCSVData } => {
  if (!data || data.length === 0) {
    return { isValid: false, error: "CSV file is empty." };
  }

  const filteredData = data.filter((row) => {
    if (!row || typeof row !== "object") return false;
    return Object.values(row).some((value) => {
      if (value === null || value === undefined) return false;
      const stringValue = String(value).trim();
      return (
        stringValue !== "" &&
        stringValue !== "null" &&
        stringValue !== "undefined"
      );
    });
  });

  if (filteredData.length < 1) {
    return {
      isValid: false,
      error: "CSV must contain at least 1 data row with valid data.",
    };
  }

  if (filteredData.length > 100) {
    return {
      isValid: false,
      error: "Validation failed: File exceeds 100 records limit.",
    };
  }

  const headers = Object.keys(filteredData[0]);
  if (headers.length < 3) {
    return { isValid: false, error: "CSV must contain at least 3 columns." };
  }

  const requiredColumns = expectedColumns || [
    "User Code",
    "Wallet Type",
    "Amount",
  ];
  const normalizedHeaders = headers.map((h) =>
    h.trim().toLowerCase().replace(/\s+/g, " ")
  );
  const missingColumns = requiredColumns.filter((col) => {
    const normalizedRequired = col.toLowerCase().replace(/\s+/g, " ");
    return !normalizedHeaders.includes(normalizedRequired);
  });
  if (missingColumns.length > 0) {
    return { isValid: false, error: "Invalid number of columns or rows" };
  }

  const records: Array<CSVRecord> = [];
  for (let i = 0; i < filteredData.length; i++) {
    const row = filteredData[i] as Record<string, unknown>;
    for (const requiredCol of requiredColumns) {
      const columnName =
        headers.find(
          (h) =>
            h.trim().toLowerCase().replace(/\s+/g, " ") ===
            requiredCol.toLowerCase().replace(/\s+/g, " ")
        ) || requiredCol;
      const value = row[columnName];
      const processedValue =
        value !== null && value !== undefined ? String(value).trim() : "";
      if (
        !processedValue ||
        processedValue === "" ||
        processedValue === "null" ||
        processedValue === "undefined"
      ) {
        return { isValid: false, error: "Validation failed" };
      }
    }

    const record: CSVRecord = {};
    headers.forEach((header) => {
      const value = row[header as keyof typeof row];
      const fieldName = header.toLowerCase().replace(/\s+/g, "_");
      let processedValue: string | number =
        value !== null && value !== undefined ? String(value).trim() : "";
      if (fieldName.includes("amount") && processedValue) {
        const numericValue = parseFloat(processedValue);
        processedValue = !isNaN(numericValue) ? numericValue : processedValue;
      }
      record[fieldName] = processedValue;
    });
    records.push(record);
  }

  if (customValidation) {
    const customError = customValidation(records);
    if (customError) {
      return { isValid: false, error: customError };
    }
  }

  return { isValid: true, parsedData: { records } };
};

const parseCSVFile = (
  file: File,
  expectedColumns?: string[],
  customValidation?: (records: Array<CSVRecord>) => string | null
): Promise<{
  isValid: boolean;
  error?: string;
  parsedData?: ParsedCSVData;
}> => {
  return new Promise((resolve) => {
    Papa.parse<Record<string, unknown>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header: string) => header.trim(),
      transform: (value: string) => {
        const trimmed = value.trim();
        return trimmed === "" ? null : trimmed;
      },
      complete: (results: Papa.ParseResult<Record<string, unknown>>) => {
        if (results.errors.length > 0) {
          resolve({
            isValid: false,
            error: `CSV parsing error: ${results.errors[0].message}`,
          });
          return;
        }
        const validation = validateCSVStructure(
          results.data,
          expectedColumns,
          customValidation
        );
        resolve(validation);
      },
      error: (error) => {
        resolve({
          isValid: false,
          error: `Failed to parse CSV file: ${error.message}`,
        });
      },
    });
  });
};

type ProcessFileResult =
  | string
  | { uploadedUrl: string; parsedData?: ParsedCSVData };

const processFile = async (
  file: File,
  setFile: React.Dispatch<React.SetStateAction<FileWithPreview | null>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>,
  setUploading: React.Dispatch<React.SetStateAction<boolean>>,
  uploadPath: string,
  expectedColumns?: string[],
  customValidation?: (records: Array<CSVRecord>) => string | null
): Promise<ProcessFileResult | undefined> => {
  const validationError = validateFile(file, expectedColumns);
  if (validationError) {
    setError(validationError);
    return;
  }

  // CSV handling
  if (expectedColumns && expectedColumns.length > 0) {
    setUploading(true);
    try {
      const csvValidation = await parseCSVFile(
        file,
        expectedColumns,
        customValidation
      );
      if (!csvValidation.isValid) {
        setError(csvValidation.error || "Invalid number of columns or rows.");
        setUploading(false);
        return;
      }
      setFile(
        Object.assign(file, {
          preview: URL.createObjectURL(file),
        })
      );
      setUploading(false);
      return { uploadedUrl: file.name, parsedData: csvValidation.parsedData };
    } catch {
      setError("Failed to process CSV file.");
      setFile(null);
      setUploading(false);
      return;
    }
  }

  setFile(
    Object.assign(file, {
      preview: URL.createObjectURL(file),
    })
  );

  setUploading(true);
  try {
    const uploadedUrl = await handleUpload(file, uploadPath);
    if (uploadedUrl) {
      return uploadedUrl;
    } else {
      setError("Upload failed.");
    }
  } catch {
    setError("Upload failed.");
  } finally {
    setUploading(false);
  }
};

const S3ImageUpload: React.FC<S3ImageUploadProps> = ({
  heading,
  disabled,
  UploadPath,
  upload_id,
  handleUpload,
  uploadedFile,
  label,
  errorMessage,
  required,
  paddingTop,
  id,
  setCustomError,
  index,
  uploadedContainerClass,
  customWidth,
  expectedColumns,
  customValidation,
}) => {
  const [, setFile] = useState<FileWithPreview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState<boolean>(false);
  const { translate, notEnglish } = useTranslation(breadcrumbsContent);
  const getAcceptedFormats = () => {
    const baseFormats: Record<string, string[]> = {
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "application/pdf": [".pdf"],
    };
    if (expectedColumns && expectedColumns.length > 0) {
      return {
        "text/csv": [".csv"],
        "application/vnd.ms-excel": [".csv"],
        "application/csv": [".csv"],
      } as Record<string, string[]>;
    }
    return baseFormats;
  };
  const getSupportedFormatsText = () => {
    if (expectedColumns && expectedColumns.length > 0) {
      return translate("Supported file type: csv format");
    }
    return translate("Supported file types: jpg, png, jpeg and pdf format");
  };
  const handleDrop = async (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[]
  ) => {
    setError(null);

    if (rejectedFiles.length > 0) {
      const rejectedFile = rejectedFiles[0].file as File;
      setError(
        validateFile(rejectedFile, expectedColumns) || "File upload failed."
      );
      return;
    }

    if (acceptedFiles.length > 0) {
      const uploadedFile = await processFile(
        acceptedFiles[0],
        setFile,
        setError,
        setUploading,
        UploadPath,
        expectedColumns,
        customValidation
      );
      if (uploadedFile !== undefined) {
        if (typeof uploadedFile === "string") {
          handleUpload(upload_id, uploadedFile);
        } else {
          handleUpload(
            upload_id,
            uploadedFile.uploadedUrl || "",
            acceptedFiles[0],
            uploadedFile.parsedData
          );
        }
      }
    }
  };

  const handleManualUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setError(null);
    if (event.target.files && event.target.files.length > 0) {
      const uploadedFile = await processFile(
        event.target.files[0],
        setFile,
        setError,
        setUploading,
        UploadPath,
        expectedColumns,
        customValidation
      );
      if (uploadedFile !== undefined) {
        
        if (typeof uploadedFile === "string") {
          handleUpload(upload_id, uploadedFile);
        } else {
          handleUpload(
            upload_id,
            uploadedFile.uploadedUrl || "",
            event.target.files[0],
            uploadedFile.parsedData
          );
        }
      }
    }
  };

  const handleClickUpload = () => {
    setError(null);
    setCustomError?.({});
    setTimeout(() => {
      const input = document.getElementById(
        id || upload_id
      ) as HTMLInputElement;
      if (input) {
        input.value = ""; // Reset value
        input.click();
      }
    }, 0); // Delay to ensure it's inside a user-initiated event
  };

  return (
    <div
      className={`flex flex-col ${paddingTop ? paddingTop : "pt-8"}`}
      dir={notEnglish ? "rtl" : "ltr"}
    >
      {label && (
        <InputLabel
          required={required}
          labelName={label || ""}
          id={label?.toLowerCase() || ""}
        />
      )}
      {heading !== "" && (
        <h1 className="text-heading3 text-primary_text underline mb-6">
          {translate(heading)}
        </h1>
      )}
      {!uploadedFile || uploading ? (
        <Dropzone
          onDrop={handleDrop}
          disabled={disabled || uploading}
          accept={getAcceptedFormats()}
          maxSize={10 * 1024 * 1024}
          multiple={false}
        >
          {({ getRootProps, isDragActive }: DropzoneState) => (
            <div
              {...getRootProps()}
              className={`${
                customWidth ? customWidth : "w-full max-w-md"
              } ${expectedColumns ? 'pr-12 pl-12 pb-9 pt-9' : 'p-6'} border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-center transition ${
                isDragActive
                  ? "border-P_light_active bg-blue-50"
                  : error || errorMessage
                  ? "border-negative bg-white"
                  : disabled
                  ? "border-disable_stroke bg-disable_fill"
                  : "border-p_light_hover bg-white"
              }`}
            >
              <input
                type="file"
                id={id || upload_id}
                data-testid={`input-${id || upload_id}`}
                disabled={disabled || uploading}
                className="hidden"
                title="Upload file input"
                aria-label="Upload file input"
                onChange={handleManualUpload}
              />
              <button
                className={` ${
                  notEnglish
                    ? "h-[52px] gap-3 px-2 w-fit"
                    : "h-[44px] gap-2 w-[110px]"
                } flex items-center justify-center rounded-lg border border-p_primary text-p_primary text-body1 transition`}
                onClick={handleClickUpload}
                aria-label="Upload Button"
                data-testid={id || upload_id}
              >
                {uploading ? (
                  <RoundedLoader />
                ) : (
                  <>
                    <div className={`-rotate-90 `}>
                      <Image src="log-out" />
                    </div>
                    {translate("Upload")}
                  </>
                )}
              </button>
              <p className="text-secondary_text text-medium mt-1.5">
                {translate("or")}
              </p>
              <p className="text-body1 text-primary_text mb-4">
                {translate("Drag & Drop")}
              </p>
              <p className="text-secondary_text text-body1">
                {translate("Max file size: 10MB")}
                <br />
                {getSupportedFormatsText()}
              </p>
            </div>
          )}
        </Dropzone>
      ) : (
        <UploadedContainer
          fileName={uploadedFile.split("/")[uploadedFile.split("/").length - 1]}
          onclickDelete={() => {
            if (expectedColumns && expectedColumns.length > 0) {
              // For CSV files, just clear local state without API call
              handleUpload(upload_id, "");
              setFile(null);
            } else {
              // For other files, call API to delete
              // handleDelete(uploadedFile);
              handleUpload(upload_id, "");
              setFile(null);
            }
          }}
          handleManualUpload={handleManualUpload}
          id={upload_id}
          index={index}
          uploadedContainerClass={uploadedContainerClass}
        />
      )}
      {(error || errorMessage) && (
        <ErrorMessage
          errorMessage={error || errorMessage || ""}
          className="text-negative"
          testId={`error-${id || upload_id}`}
        />
      )}
      {/* {!error && errorMessage && <ErrorMessage errorMessage={errorMessage || ''} className="text-negative" />} */}
    </div>
  );
};

export default S3ImageUpload;
